
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { MapPin, Target, Calendar, Plus } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface CareerGoal {
  id: string;
  title: string;
  description: string;
  targetDate: string;
  status: 'pending' | 'in-progress' | 'completed';
}

const CareerMap360: React.FC = () => {
  const { toast } = useToast();
  const [goals, setGoals] = useState<CareerGoal[]>([
    {
      id: '1',
      title: 'Complete JavaScript Certification',
      description: 'Learn advanced JavaScript concepts and get certified',
      targetDate: '2024-08-01',
      status: 'in-progress'
    }
  ]);
  const [newGoal, setNewGoal] = useState({
    title: '',
    description: '',
    targetDate: ''
  });

  const addGoal = () => {
    if (!newGoal.title) return;
    
    const goal: CareerGoal = {
      id: Date.now().toString(),
      ...newGoal,
      status: 'pending'
    };
    
    setGoals([...goals, goal]);
    setNewGoal({ title: '', description: '', targetDate: '' });
    toast({
      title: "Career Goal Added!",
      description: "Your new career goal has been added to your roadmap."
    });
  };

  const updateGoalStatus = (id: string, status: CareerGoal['status']) => {
    setGoals(goals.map(goal => 
      goal.id === id ? { ...goal, status } : goal
    ));
    toast({
      title: "Goal Updated!",
      description: `Goal status changed to ${status}.`
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-orange-500" />
            Career Map 360
          </CardTitle>
          <CardDescription>
            Plan and track your career journey with our comprehensive roadmap tool.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add New Goal */}
          <div className="border rounded-lg p-4 bg-orange-50">
            <h3 className="font-semibold mb-3">Add New Career Goal</h3>
            <div className="space-y-3">
              <div>
                <Label htmlFor="goal-title">Goal Title</Label>
                <Input
                  id="goal-title"
                  value={newGoal.title}
                  onChange={(e) => setNewGoal({...newGoal, title: e.target.value})}
                  placeholder="e.g., Learn Python Programming"
                />
              </div>
              <div>
                <Label htmlFor="goal-description">Description</Label>
                <Textarea
                  id="goal-description"
                  value={newGoal.description}
                  onChange={(e) => setNewGoal({...newGoal, description: e.target.value})}
                  placeholder="Describe your goal and how you plan to achieve it"
                  rows={3}
                />
              </div>
              <div>
                <Label htmlFor="goal-date">Target Date</Label>
                <Input
                  id="goal-date"
                  type="date"
                  value={newGoal.targetDate}
                  onChange={(e) => setNewGoal({...newGoal, targetDate: e.target.value})}
                />
              </div>
              <Button onClick={addGoal} className="bg-orange-500 hover:bg-orange-600">
                <Plus className="h-4 w-4 mr-2" />
                Add Goal
              </Button>
            </div>
          </div>

          {/* Goals List */}
          <div className="space-y-3">
            <h3 className="font-semibold">Your Career Goals</h3>
            {goals.map((goal) => (
              <div key={goal.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-medium">{goal.title}</h4>
                  <span className={`px-2 py-1 rounded text-xs ${
                    goal.status === 'completed' ? 'bg-green-100 text-green-800' :
                    goal.status === 'in-progress' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {goal.status.replace('-', ' ')}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mb-2">{goal.description}</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    {goal.targetDate}
                  </div>
                  <div className="flex gap-2">
                    {goal.status !== 'in-progress' && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => updateGoalStatus(goal.id, 'in-progress')}
                      >
                        Start
                      </Button>
                    )}
                    {goal.status !== 'completed' && (
                      <Button 
                        size="sm" 
                        className="bg-green-500 hover:bg-green-600"
                        onClick={() => updateGoalStatus(goal.id, 'completed')}
                      >
                        Complete
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CareerMap360;
